import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, EmptyState, Spinner, CardSkeleton, IconButton, Chip, BackButton, cx,
} from "../components/UI";
import { DrawingViewerModal, getFileIcon, getFileBadgeStyle, formatBytes } from "../components/DrawingViewer";
import DrawingUpload from "../components/DrawingUpload";
import { deleteDrawingFile } from "../utils/firebase";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import toast from "react-hot-toast";
import { Upload, Eye, Download, History, Trash2, Ruler } from "lucide-react";

export default function AdminSiteDrawings() {
  const { siteId } = useParams();
  const { user } = useAuth();
  const [site, setSite] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [viewDrawing, setViewDrawing] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  const fetchDrawings = useCallback(async () => {
    setLoading(true);
    try {
      const [siteRes, drawRes] = await Promise.all([
        api.get(`/sites/admin/${siteId}`),
        api.get(`/drawings/site/${siteId}`),
      ]);
      setSite(siteRes.data.data);
      setDrawings(drawRes.data.data || []);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [siteId]);

  useEffect(() => { fetchDrawings(); }, [fetchDrawings]);

  const openHistory = async (d) => {
    setHistoryModal({ drawingCode: d.drawingCode, title: d.title });
    setHistLoading(true);
    try {
      const { data } = await api.get(`/drawings/site/${siteId}/history/${d.drawingCode}`);
      setHistory(data.data || []);
    } catch { toast.error("Failed"); }
    finally { setHistLoading(false); }
  };

  const handleDelete = async (d) => {
    if (!window.confirm(`Delete "${d.title}" v${d.version}?`)) return;
    try {
      const { data } = await api.delete(`/drawings/${d._id}`);
      if (data.data?.firebasePath) await deleteDrawingFile(data.data.firebasePath);
      toast.success("Deleted");
      fetchDrawings();
    } catch { toast.error("Delete failed"); }
  };

  const downloadCls = "inline-grid place-items-center w-8 h-8 rounded-lg border border-ink-300 bg-white text-ink-600 hover:border-brand-400 hover:text-brand-600 hover:bg-brand-50 transition-all shrink-0";

  return (
    <AdminLayout>
      <div className="space-y-5">

        <div className="flex flex-col gap-4">
          <nav className="flex items-center gap-2 text-xs text-ink-400 flex-wrap">
            <Link to="/admin/sites" className="hover:text-ink-700 transition-colors">Sites</Link>
            <span>/</span>
            <Link to={`/admin/sites/${siteId}`} className="hover:text-ink-700 transition-colors truncate max-w-[180px]">{site?.name || "…"}</Link>
            <span>/</span>
            <span className="text-ink-600 font-medium">Drawings</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <BackButton to={`/admin/sites/${siteId}`} />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-ink-900 font-display truncate">Drawings</h1>
                <p className="text-ink-400 text-sm mt-0.5 truncate">{site?.name || "…"}</p>
              </div>
            </div>
            <Button onClick={() => setUploadModal(true)} icon={<Upload size={16} />} className="w-full sm:w-auto">Upload Drawing</Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <CardSkeleton key={i} lines={2} />)}</div>
        ) : drawings.length === 0 ? (
          <Card>
            <EmptyState icon={<Ruler size={22} />} title="No drawings for this site"
              description="Upload DWG, DXF or PDF files — the crew sees them immediately."
              action={<Button onClick={() => setUploadModal(true)} icon={<Upload size={16} />}>Upload Drawing</Button>} />
          </Card>
        ) : (
          <div className="space-y-3">
            {drawings.map((d) => (
              <Card key={d._id} className="p-4" hover>
                {/* actions lead the row so they're the first thing in reach */}
                <div className="flex items-center gap-1.5 flex-wrap pb-3 mb-3 border-b border-ink-100 sm:hidden">
                  <Button size="xs" variant="outline" icon={<Eye size={13} />} onClick={() => setViewDrawing(d)}>Preview</Button>
                  <a href={d.firebaseURL} download={d.fileName} target="_blank" rel="noopener noreferrer"
                    title="Download" aria-label="Download" className={downloadCls}><Download size={15} /></a>
                  <IconButton size="sm" variant="outline" icon={<History size={15} />} onClick={() => openHistory(d)} title="Versions" />
                  <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(d)} title="Delete" className="ml-auto" />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                  <div className="hidden sm:flex items-center gap-1.5 shrink-0 order-first">
                    <Button size="xs" variant="outline" icon={<Eye size={13} />} onClick={() => setViewDrawing(d)}>Preview</Button>
                    <a href={d.firebaseURL} download={d.fileName} target="_blank" rel="noopener noreferrer"
                      title="Download" aria-label="Download" className={downloadCls}><Download size={15} /></a>
                    <IconButton size="sm" variant="outline" icon={<History size={15} />} onClick={() => openHistory(d)} title="Versions" />
                    <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(d)} title="Delete" />
                  </div>

                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <span className={cx("w-11 h-11 rounded-xl border grid place-items-center text-xl shrink-0", getFileBadgeStyle(d.fileType))}>
                      {getFileIcon(d.fileType)}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink-900 font-semibold truncate">{d.title}</span>
                        <span className={cx("text-[10px] px-1.5 py-0.5 rounded border uppercase font-mono", getFileBadgeStyle(d.fileType))}>{d.fileType}</span>
                        <span className="text-[10px] bg-ink-100 text-ink-700 border border-ink-200 px-1.5 py-0.5 rounded-full font-mono">v{d.version}</span>
                        <span className="text-[10px] text-ink-400 font-mono bg-ink-50 px-2 py-0.5 rounded">{d.drawingCode}</span>
                      </div>
                      <div className="flex gap-x-3 gap-y-1 mt-1.5 text-xs text-ink-400 flex-wrap">
                        <span className="tnum">{formatBytes(d.fileSize)}</span>
                        <span>by {d.uploadedByName || d.uploadedByRole}</span>
                        <span>{new Date(d.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      </div>
                      {d.tags?.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">{d.tags.map(t => <Chip key={t} tone="brand">#{t}</Chip>)}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Modal open={uploadModal} onClose={() => setUploadModal(false)} title="Upload Drawing"
        subtitle={site?.name} icon={<Upload size={18} />} size="lg">
        <DrawingUpload
          siteId={siteId}
          adminId={user?._id || user?.id}
          existingCodes={drawings.map((d) => d.drawingCode)}
          onSuccess={() => { setUploadModal(false); fetchDrawings(); }}
          onCancel={() => setUploadModal(false)}
        />
      </Modal>

      <Modal open={!!historyModal} onClose={() => setHistoryModal(null)} title="Version History"
        subtitle={historyModal?.title} icon={<History size={18} />} size="lg">
        {histLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (
          <div className="space-y-3">
            {history.map((d) => (
              <div key={d._id} className={cx("p-3.5 rounded-2xl border",
                d.isLatest ? "border-emerald-200 bg-emerald-50/50" : "border-ink-200 bg-ink-50/50")}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={cx("w-9 h-9 rounded-xl grid place-items-center text-xs font-bold font-mono shrink-0",
                      d.isLatest ? "bg-emerald-600 text-white" : "bg-ink-200 text-ink-700")}>v{d.version}</span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-ink-800 text-sm font-medium truncate">{d.title}</span>
                        {d.isLatest && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold">Latest</span>}
                      </div>
                      <div className="text-xs text-ink-400 mt-0.5">
                        {formatBytes(d.fileSize)} · {d.uploadedByName || d.uploadedByRole} ·{" "}
                        {new Date(d.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <IconButton size="sm" variant="outline" icon={<Eye size={15} />} onClick={() => setViewDrawing(d)} title="Preview" />
                    <a href={d.firebaseURL} target="_blank" rel="noopener noreferrer" download={d.fileName}
                      title="Download" aria-label="Download" className={downloadCls}><Download size={15} /></a>
                    <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(d)} title="Delete" />
                  </div>
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-ink-100">
              <p className="text-sm font-semibold text-ink-700 mb-3">Upload a new version</p>
              <DrawingUpload
                siteId={siteId}
                adminId={user?._id || user?.id}
                existingCodes={[historyModal?.drawingCode].filter(Boolean)}
                onSuccess={() => { fetchDrawings(); openHistory({ drawingCode: historyModal.drawingCode, title: historyModal.title }); }}
                onCancel={() => setHistoryModal(null)}
              />
            </div>
          </div>
        )}
      </Modal>

      <DrawingViewerModal drawing={viewDrawing} onClose={() => setViewDrawing(null)} />
    </AdminLayout>
  );
}
